import { Box, Button, Tabs } from "@mantine/core"
import { DownloadSimpleIcon, PlusIcon, TruckIcon } from "@phosphor-icons/react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { OrderApi, SplitApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { useSalesActions } from "../../components/order/order-actions"
import { usePermission } from "../../components/permission"
import { BulkActionBar, ReasonTooltip } from "../../components/ui"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { SupplierSplitList, SupplierSplitListRef } from "../supplier-split/list"
import { SupplierSalesList, SupplierSalesListRef } from "./list"

export function SupplierSalesTabs() {
  const { t } = useTranslation(["supplierSales", "common"])
  const breadcrumbs = useBreadcrumb(t)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("DELIVERY")
  const [checked, setChecked] = useState<string[]>([])
  const [actions, setActions] = useState<string[]>([])
  const [splitChecked, setSplitChecked] = useState<string[]>([])
  const [action, setAction] = useState<string[]>([])
  const [excelBusy, setExcelBusy] = useState<boolean>(false)
  const supplierSalesListRef = useRef<SupplierSalesListRef>(null)
  const supplierSplistListRef = useRef<SupplierSplitListRef>(null)

  const ORD_LIST = usePermission("ORD_LIST")
  const ORD_SPLIT = usePermission("ORD_SPLIT")
  const ORD_SPLIT_DN_ASSIGN = usePermission("ORD_SPLIT_DN_ASSIGN")
  const { permissionState } = useSalesActions()
  const createDeliveryPerm = permissionState("deliveryCreate")
  const newOrderPerm = permissionState("edit") // ORD_SAVE or ORD_SEND (BE order/create.ts)

  const isDelivery = activeTab === "DELIVERY"
  const selected = isDelivery ? checked : splitChecked

  const downloadExcel = async () => {
    setExcelBusy(true)
    try {
      if (isDelivery) {
        const { url } = await OrderApi.excel({
          filter: supplierSalesListRef.current?.getFilters(),
          order: supplierSalesListRef.current?.getOrder(),
        })
        window.open(url, "_blank")
      } else {
        const { url } = await SplitApi.excel({
          filter: supplierSplistListRef.current?.getFilters(),
          order: supplierSplistListRef.current?.getOrder(),
        })
        window.open(url, "_blank")
      }
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setExcelBusy(false)
    }
  }

  // «Хүргэлт үүсгэх» / «Хуваарилах» act on the selected rows, so they live in the selection bar.
  const bulkPermReason = isDelivery ? createDeliveryPerm.reason : ORD_SPLIT_DN_ASSIGN.reason

  const excelReason = isDelivery ? ORD_LIST.reason : ORD_SPLIT.reason

  return (
    <PageLayout
      title={t("sales.title")}
      breadcrumb={breadcrumbs}
      extra={[
        <ReasonTooltip key="excel" reason={excelReason}>
          <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} loading={excelBusy} onClick={downloadExcel}>
            {t("sales.excelDownload")}
          </Button>
        </ReasonTooltip>,
        <ReasonTooltip key="new" reason={newOrderPerm.reason}>
          <Button leftSection={<PlusIcon size={16} />} onClick={() => navigate("/supplier-sales/new")}>
            {t("sales.newOrder")}
          </Button>
        </ReasonTooltip>,
      ]}>
      {!ORD_LIST.hasAccess ? (
        <Box>{ORD_LIST.accessDenied()}</Box>
      ) : (
        <Tabs value={activeTab} onChange={(e) => setActiveTab(e as string)} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="DELIVERY">{t("sales.allOrders")}</Tabs.Tab>
            <Tabs.Tab value="SPLIT">{t("sales.partialDelivery")}</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="DELIVERY">
            <SupplierSalesList
              setChecked={setChecked}
              actions={actions}
              checked={checked}
              setActions={setActions}
              onCreate={() => navigate("/supplier-sales/new")}
              ref={supplierSalesListRef}
            />
          </Tabs.Panel>
          <Tabs.Panel value="SPLIT">
            <SupplierSplitList
              setSplitChecked={setSplitChecked}
              action={action}
              splitChecked={splitChecked}
              setAction={setAction}
              success={() => setActiveTab("SPLIT")}
              ref={supplierSplistListRef}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      <BulkActionBar
        selectedCount={selected.length}
        onClear={() => (isDelivery ? setChecked([]) : setSplitChecked([]))}
        actions={[{
          key: "bulk",
          label: t(isDelivery ? "sales.createDelivery" : "sales.distribute"),
          icon: <TruckIcon size={16} />,
          primary: true,
          reason: bulkPermReason,
          onClick: () => (isDelivery ? setActions(["delivery", checked as never]) : setAction(["split", splitChecked as never])),
        }]}
      />
    </PageLayout>
  )
}

const useBreadcrumb = (t: (key: string) => string) => [
  { to: "/", label: t("common:breadcrumb.home") },
  { label: t("sales.breadcrumb") },
]
